import { useMemo, useState } from "react";
import { addToast, button, Button, CardBody, CardFooter, CardHeader, Link } from "@heroui/react";
import { InfoIcon, UserIcon } from "@phosphor-icons/react";
import { SocialLogin } from "@capgo/capacitor-social-login";

import googleLogo from "@/assets/images/google-logo.png";
import appleLogo from "@/assets/images/apple-logo.svg";
import { BlurredCard } from "@/shared/components/ui/BlurredCard";
import { supabase } from "@/services/supabase/client";
import { ICON_SIZE } from "@/config/constants";
import { useBoundStore } from "@/app/store";
import { useShallow } from "zustand/shallow";
import { Logo } from "@/shared/components/common/Logo";
import { usePageLayout } from "@/app/hooks/usePageLayout";
import { saveAppleFullName } from "@/services/api/utils/auth";

export const DEEP_LINK_GOOGLE = "com.inkrows.app://loginWithGoogle";

export function SignInRoute() {
  const [isGuestLoading, setIsGuestLoading] = useState(false);
  const { isNativeDevice, platform } = useBoundStore(
    useShallow((state) => ({
      isNativeDevice: state.device.isNative,
      platform: state.device.platform,
    }))
  );

  const handleGuestSignIn = async () => {
    setIsGuestLoading(true);
    try {
      const { error } = await supabase.auth.signInAnonymously();
      if (error) throw error;
    } catch (error) {
      console.error("Guest sign-in error:", error);
      addToast({
        color: "danger",
        title: "Guest sign-in failed",
        description: error instanceof Error ? error.message : "Failed to sign in as guest",
      });
    } finally {
      setIsGuestLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: isNativeDevice ? DEEP_LINK_GOOGLE : undefined,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
  };

  const handleAppleSignIn = async () => {
    // Web: use Supabase OAuth redirect flow (SocialLogin popup can't resolve on web)
    if (platform !== "ios") {
      await supabase.auth.signInWithOAuth({
        provider: "apple",
        options: { redirectTo: "https://fshxtcrsqkcmnzssayar.supabase.co/auth/v1/callback" },
      });
      return;
    }

    try {
      // Initialize SocialLogin for Apple authentication
      try {
        await SocialLogin.initialize({ apple: {} });
      } catch (error) {
        console.error("Failed to initialize SocialLogin:", error);
      }

      const result = await SocialLogin.login({
        provider: "apple",
        options: {
          scopes: ["email", "name"],
        },
      });

      if (result.result?.idToken) {
        const { data: authData, error: supabaseError } = await supabase.auth.signInWithIdToken({
          provider: "apple",
          token: result.result.idToken,
        });

        if (supabaseError) throw supabaseError;

        // Save full name metadata if available (only on first sign-in)
        if (result.result.profile) {
          await saveAppleFullName(result.result.profile, authData?.user?.id);
        }
      } else {
        throw new Error("No identityToken provided by Apple");
      }
    } catch (error: any) {
      const errorMessage = error?.message || error?.errorMessage || "";
      const isUserCanceled =
        error?.code === "ERR_REQUEST_CANCELED" ||
        error?.code === "userCanceled" ||
        error?.code === "1" ||
        error?.code === "1001" ||
        errorMessage.toLowerCase().includes("canceled") ||
        errorMessage.includes("error 1001");

      if (isUserCanceled) {
        console.log("Apple sign-in canceled by user");
      } else {
        console.error("Apple sign-in error:", error);
        if (error.__isAuthError) {
          console.error("Supabase Auth Error:", error.message, error.status);
        }
      }
    }
  };

  const pageLayoutConfig = useMemo(
    () => ({
      navbar: {
        actions: [
          <Link
            className={button({ size: "sm", variant: "flat" })}
            key="about-us"
            href="https://www.inkrows.com"
            isExternal
          >
            <InfoIcon size={ICON_SIZE.sm} />
            About us
          </Link>,
        ],
        hideBack: true,
        title: "InkRows",
      },
    }),
    []
  );
  usePageLayout(pageLayoutConfig);

  return (
    <>
      <BlurredCard shadow="sm" className="mx-auto mt-8 max-w-md p-4">
        <CardHeader className="flex-col justify-center gap-3 text-2xl font-semibold">
          <Logo size={64} />
          Welcome to InkRows!
        </CardHeader>
        <CardBody className="mt-4">
          <p>
            {platform === "ios"
              ? "Sign in to sync your notes and tasks across all your devices, or continue as a guest to get started immediately."
              : "Sign in to sync your notes and tasks across all your devices."}
          </p>
        </CardBody>
        <CardFooter className="flex-col">
          <Button size="lg" fullWidth variant="flat" onPress={handleGoogleSignIn}>
            <img alt="" src={googleLogo} width={22} height={22} />
            Sign in with Google
          </Button>
          {!(isNativeDevice && platform === "android") && (
            <Button size="lg" fullWidth variant="flat" onPress={handleAppleSignIn} className="mt-3">
              <img className="dark:invert" alt="" src={appleLogo} width={22} height={22} />
              Sign in with Apple
            </Button>
          )}
          {platform === "ios" && (
            <>
              <div className="my-3 flex w-full items-center gap-3">
                <div className="bg-default-200 h-px flex-1" />
                <span className="text-default-400 text-xs font-medium uppercase">or</span>
                <div className="bg-default-200 h-px flex-1" />
              </div>
              <Button
                size="lg"
                fullWidth
                variant="bordered"
                isLoading={isGuestLoading}
                onPress={handleGuestSignIn}
                startContent={!isGuestLoading ? <UserIcon size={ICON_SIZE.md} /> : null}
              >
                Continue as Guest
              </Button>
            </>
          )}
          <p className="text-default-500 mt-4 text-center text-sm">
            By signing in, you agree to our{" "}
            <Link
              className="text-sm text-inherit"
              href="https://www.inkrows.com/legal/terms-and-conditions"
              rel="noopener noreferrer"
              target="_blank"
              underline="always"
            >
              Terms and Conditions
            </Link>{" "}
            and{" "}
            <Link
              className="text-sm text-inherit"
              href="https://www.inkrows.com/legal/privacy-policy"
              rel="noopener noreferrer"
              target="_blank"
              underline="always"
            >
              Privacy Policy
            </Link>
          </p>
        </CardFooter>
      </BlurredCard>
    </>
  );
}
